import { MultiVoucherUsageLeftPanelSide } from 'app/src/features/Shopping/Direct/MultiVoucherUsage/components/MultiVoucherUsageLeftPanelSide'
import { MultiVoucherUsageRightPanelSide } from 'app/src/features/Shopping/Direct/MultiVoucherUsage/components/MultiVoucherUsageRightPanelSide'
import type { refundApi } from 'app/src/services/welfareportal'
import { euroFormatterIt, formatDate } from 'app/src/shared/helpers'
import { Checkbox, type CheckboxChangeEvent } from 'primereact/checkbox'
import { RadioButton, type RadioButtonChangeEvent } from 'primereact/radiobutton'
import { useTranslation } from 'react-i18next'

export function MultiVoucherUsageConfirmation(props: {
  refund: refundApi.Refund
  additionalRadioKeys: string[] | undefined
  privacyStatementAccept: boolean
  voucherAmount: number
  setPrivacyStatementAccept: (flag: boolean) => void
  setAdditionalRadioKeys: (radioGroupIndex: number, additionalPropSelected: string) => void
  formData: {
    requestedRefund: number
    invoiceId: string
    documentDate: string
    vendorName: string
    vendorTaxId: string
    invoiceTotal: number
  }
}) {
  const { t } = useTranslation('shopping', { keyPrefix: 'Direct.MultiVoucher.Usage' })
  return (
    <div>
      <div className='card grid grid-cols-1 divide-x-0 divide-y-2 p-5 lg:grid-cols-12 lg:divide-x-2 lg:divide-y-0'>
        <MultiVoucherUsageLeftPanelSide refund={props.refund} />
        <MultiVoucherUsageRightPanelSide refund={props.refund} requestedRefund={props.formData.requestedRefund} funnelMode />
      </div>
      <div className='mt-4 grid rounded-md bg-[var(--card-background)] p-4'>
        <div>
          <h3 className='truncate'>{t('Funnel.Steps.ConfirmationStep.DetailBox.title')}</h3>
        </div>
        <div className='mt-3 grid grid-cols-1 md:grid-cols-2'>
          <div className='grid-rows-2'>
            <div className='uppercase'>{t('Funnel.Steps.ConfirmationStep.DetailBox.beneficiaryNameSurname')}</div>
            <div className='mt-2'>
              <h3 className='truncate'>
                {props.refund.subject ? `${props.refund.subject.firstName} ${props.refund.subject.lastName}` : ''}
              </h3>
            </div>
          </div>
          <div className='grid-rows-2'>
            <div className='uppercase'>{t('Funnel.Steps.ConfirmationStep.DetailBox.beneficiaryFiscalCode')}</div>
            <div className='mt-2'>
              <h3 className='truncate'> {props.refund.subject ? `${props.refund.subject.fiscalCode}` : ''}</h3>
            </div>
          </div>
        </div>
        <div className='mt-3 grid grid-cols-1 md:grid-cols-2'>
          <div className='grid-rows-2'>
            <div className='uppercase'>{t('Funnel.Steps.ConfirmationStep.DetailBox.requestDate')}</div>
            <div className='mt-2'>
              <h3 className='truncate'>{formatDate(new Date().toISOString())}</h3>
            </div>
          </div>
        </div>
        <div className='mt-3 grid grid-cols-1 md:grid-cols-2'>
          <div className='grid-rows-2'>
            <div className='uppercase'>{t('Funnel.Steps.ConfirmationStep.DetailBox.requestedRefund')}</div>
            <div className='mt-2'>
              <h3 className='truncate'>{euroFormatterIt.format(props.formData.requestedRefund)}</h3>
            </div>
          </div>
          <div className='grid-rows-2'>
            <div className='uppercase'>{t('Funnel.Steps.ConfirmationStep.DetailBox.voucherAmount')}</div>
            <div className='mt-2'>
              <h3 className='truncate'>{euroFormatterIt.format(props.voucherAmount)}</h3>
            </div>
          </div>
        </div>
      </div>
      {/* DYNAMIC CHECKBOX */}
      {props.refund?.rule?.additionalAttributes?.radio && (
        <div className='mt-6'>
          {props.refund.rule.additionalAttributes.radio.map((radio, radioIndex) => (
            <div key={radio.title ?? ''} className='mt-4'>
              {radio.title && <div dangerouslySetInnerHTML={{ __html: radio.title }} />}
              <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
                {radio.attrs?.map((radioItem) =>
                  radioItem.name ? (
                    <div
                      key={radioItem.name}
                      className='justify-left mt-4 flex min-h-20 items-center rounded bg-[var(--card-background)] p-2'
                    >
                      <div className='flex items-center'>
                        <RadioButton
                          inputId={radioItem.name}
                          name={radioItem.name}
                          value={radioItem.name}
                          onChange={(e: RadioButtonChangeEvent) => {
                            if (typeof e.checked !== 'undefined' && e.checked) {
                              props.setAdditionalRadioKeys(radioIndex, radioItem.name!)
                            }
                          }}
                          checked={props.additionalRadioKeys?.[radioIndex] === radioItem.name}
                          required
                        />
                        <label htmlFor={radioItem.name} className='ml-2 cursor-pointer'>
                          <div dangerouslySetInnerHTML={{ __html: radioItem.text ?? '' }} />
                        </label>
                      </div>
                    </div>
                  ) : (
                    <></>
                  ),
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {props.refund?.rule?.termsAndConditions && (
        <>
          <div className='mt-4'>
            <div>
              <div dangerouslySetInnerHTML={{ __html: props.refund?.rule?.termsAndConditions }} />
            </div>
          </div>
          <div className='justify-content-center mt-[30px] flex flex-wrap gap-3'>
            <div className='align-items-center flex cursor-pointer items-center'>
              <Checkbox
                required
                inputId='privacyStatementAccept'
                checked={props.privacyStatementAccept}
                onChange={(event: CheckboxChangeEvent) => {
                  props.setPrivacyStatementAccept(typeof event.checked !== 'undefined' ? event.checked : false)
                }}
              />
              <label
                htmlFor='privacyStatementAccept'
                className='ml-2 cursor-pointer'
                dangerouslySetInnerHTML={{ __html: t('Funnel.Steps.ConfirmationStep.DetailBox.privacyStatement') }}
              ></label>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
